import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Radio,
} from "@mui/material";
import { X, MagnifyingGlass, CaretDown } from "phosphor-react";
import { fpatch, fpost } from "../../../../API/callsAPI";
//Validators
import { useFormik } from "formik";
import * as yup from "yup";

const fieldStyles = {
  backgroundColor: "#252A38",
  borderRadius: "12px",
  border: "1px solid rgba(255, 255, 255, 0.08)",
  color: "#F9F9F9",
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  "& .MuiInputBase-input": { color: "#F9F9F9" },
  "& .MuiSelect-icon": { color: "#B1B5C9" },
};

const AddOrEditWebhookModal = ({
  handleClose,
  setSnackValue,
  projectId,
  eventsList,
  handleRefreshCounter,
  editData,
}) => {
  // const [enabled, setEnabled] = useState(true);
  const [name, setName] = useState("");
  const [payloadUrl, setPayloadUrl] = useState("");
  const [secret, setSecret] = useState("");
  const [eventSearch, setEventSearch] = useState("");
  const [selectedEvents, setSelectedEvents] = useState([]);

  const NewWebhookValidationScheme = yup.object({
    name: yup.string("String required").required("This is a required field"),
    payloadUrl: yup
      .string("String required")
      .required("This is a required field"),
    secret: yup.string("String required"),
  });

  const formik = useFormik({
    initialValues: {
      name: editData ? editData.name : "",
      payloadUrl: editData ? editData.url : "",
      secret: editData ? editData.headers?.Authorization : "",
    },
    validationSchema: NewWebhookValidationScheme,

    onSubmit: async (values) => {
      if (editData) {
        try {
          const payloadObj = {
            project: projectId,
            name: values.name,
            content_type: selectedEvents[0].contentType,
            action_type: selectedEvents[0].actiontype,
            url: values.payloadUrl,
            http_method: "POST",
            headers: {
              Authorization: values.secret,
            },
          };
          const res = await fpatch({
            url: `webhooks/${editData.id}/`,
            data: payloadObj,
          });
          if (res.status === 200) {
            setSnackValue?.({
              isOpen: true,
              message: "Webhook updated successfully",
              isError: false,
            });
          }
        } catch (error) {
          console.log(error);
        } finally {
          handleRefreshCounter();
          handleClose?.();
        }
      } else {
        try {
          const payloadObj = {
            project: projectId,
            name: values.name,
            content_type: selectedEvents[0].contentType,
            action_type: selectedEvents[0].actiontype,
            url: values.payloadUrl,
            http_method: "POST",
            headers: {
              Authorization: values.secret,
            },
          };
          const res = await fpost({
            url: `webhooks/`,
            data: payloadObj,
          });
          if (res.status === 201) {
            setSnackValue?.({
              isOpen: true,
              message: "Webhook added successfully",
              isError: false,
            });
          }
        } catch (error) {
          console.log(error);
        } finally {
          handleRefreshCounter();
          handleClose?.();
        }
      }
    },
  });

  useEffect(() => {
    if (editData) {
      setSelectedEvents([
        {
          contentType: editData.content_type,
          actiontype: editData.action_type,
        },
      ]);
    }
  }, [editData]);

  return (
    <Box
      sx={{
        width: { xs: "95%", sm: "90%", md: "1000px" },
        height: "auto",
        bgcolor: "#1B1D28",
        borderRadius: "24px",
        p: 4,
      }}
    >
      <form onSubmit={formik.handleSubmit}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography
            sx={{ fontSize: "24px", fontWeight: 500, color: "#F9F9F9" }}
          >
            {editData ? "Edit Webhook" : "Add Webhook"}
          </Typography>
          <IconButton onClick={handleClose} sx={{ color: "#B1B5C9" }}>
            <X size={24} />
          </IconButton>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1.05fr 0.95fr" },
            gap: 3,
            paddingBottom: "10px",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <Typography sx={{ fontSize: "14px", color: "#B1B5C9", mb: 1 }}>
                Name<span style={{ color: "#E41F1F" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                name="name"
                onChange={formik.handleChange}
                value={formik.values.name}
                placeholder="Give this webhook a name"
                sx={fieldStyles}
              />
            </Box>

            <Box>
              <Typography sx={{ fontSize: "14px", color: "#B1B5C9", mb: 1 }}>
                Payload URL<span style={{ color: "#E41F1F" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder="https://example.com/postreceive"
                name="payloadUrl"
                onChange={formik.handleChange}
                value={formik.values.payloadUrl}
                sx={fieldStyles}
              />
            </Box>

            <Box>
              <Typography sx={{ fontSize: "14px", color: "#B1B5C9", mb: 1 }}>
                Secret
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder=""
                name="secret"
                onChange={formik.handleChange}
                value={formik.values.secret}
                sx={fieldStyles}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Typography
              sx={{ fontSize: "14px", fontWeight: 500, color: "#F9F9F9" }}
            >
              Select which events trigger the webhook
            </Typography>

            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Search events"
              value={eventSearch}
              onChange={(e) => setEventSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MagnifyingGlass size={16} color="#B1B5C9" />
                  </InputAdornment>
                ),
              }}
              sx={{
                ...fieldStyles,
                mb: 1,
              }}
            />
            <div
              style={{
                height: "400px",
                overflowY: "auto",
                padding: "0px 6px 10px 0px",
              }}
            >
              {eventsList
                .filter((event) =>
                  event.label.toLowerCase().includes(eventSearch.toLowerCase()),
                )
                .map((group) => (
                  <Accordion
                    key={group.content_type}
                    defaultExpanded={group.label === "Assets"}
                    sx={{
                      bgcolor: "#252A38",
                      color: "#F9F9F9",
                      borderRadius: "10px !important",
                      boxShadow: "none",
                      border: "1px solid rgba(255,255,255,0.08)",
                      "&:not(:last-of-type)": { mb: 1 },
                      "& .MuiAccordionSummary-root": {
                        minHeight: "max-content",
                        height: "45px",
                      },
                      "& .MuiAccordionSummary-content": {
                        margin: 0,
                      },
                      "& .MuiAccordionSummary-expandIconWrapper": {
                        color: "#B1B5C9",
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<CaretDown size={18} />}
                      sx={{ padding: "12px" }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          width: "97%",
                        }}
                      >
                        <Typography sx={{ fontSize: "14px" }}>
                          {group.label}
                        </Typography>
                        {selectedEvents[0]?.contentType ===
                          group.content_type && (
                          <Typography
                            sx={{
                              fontSize: "12px",
                              background: "#6C65D929",
                              border: "1px solid #6C65D97A",
                              borderRadius: "100px",
                              padding: "2px 8px",
                            }}
                          >
                            1 Selected
                          </Typography>
                        )}
                      </div>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{
                        padding: "0px",
                        borderTop: "1px solid #3F404A",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        {group.actions.map((item) => (
                          <Box
                            key={item.value}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              cursor: "pointer",
                              padding: "8px 12px 4px 12px",
                              backgroundColor:
                                selectedEvents[0]?.actiontype === item.value &&
                                selectedEvents[0]?.contentType ===
                                  group.content_type
                                  ? "#6C65D914"
                                  : "transparent",
                              "&:hover": {
                                backgroundColor: "#34394A",
                              },
                            }}
                            onClick={() =>
                              setSelectedEvents([
                                {
                                  actiontype: item.value,
                                  contentType: group.content_type,
                                },
                              ])
                            }
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Radio
                                size="small"
                                checked={
                                  selectedEvents[0]?.actiontype ===
                                    item.value &&
                                  selectedEvents[0]?.contentType ===
                                    group.content_type
                                }
                                onChange={() =>
                                  setSelectedEvents([
                                    {
                                      actiontype: item.value,
                                      contentType: group.content_type,
                                    },
                                  ])
                                }
                                sx={{
                                  paddingLeft: "0px",
                                  marginLeft: "0px",
                                  color: "#6C65D9",
                                  "&.Mui-checked": { color: "#6C65D9" },
                                }}
                              />
                              <Typography
                                sx={{ color: "#F9F9F9", fontSize: "14px" }}
                              >
                                {item.label}
                              </Typography>
                            </Box>
                            <Typography
                              sx={{
                                color: "#606479",
                                fontSize: "12px",
                              }}
                            >
                              {item.value}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))}
            </div>
          </Box>
        </Box>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Button
            variant="contained"
            type="submit"
            value="Submit"
            sx={{
              mt: 3,
              bgcolor: "#6C65D9",
              color: "#FFFFFF",
              textTransform: "none",
              borderRadius: "14px",
              py: 1.5,
              fontSize: "14px",
              fontWeight: 700,
              "&:hover": { bgcolor: "#5a54c4" },
            }}
          >
            {editData ? "Save" : "Add webhook"}
          </Button>
        </div>
      </form>
    </Box>
  );
};

export default AddOrEditWebhookModal;
